import { ref, toRef } from 'vue';
import type { ArrayElementType } from '@/core/types/gengeric';
import { useLockResource } from '@/core/composables/useLockResource';
import {
  type AddTariffScheduleInput,
  type EditTariffScheduleUpdateMutationVariables,
  type GetPlacingVariantsQuery,
  type GetTariffScheduleQuery,
  type PvmCompleteMutationVariables,
  type PvmGenerateMutationVariables,
  type PvmStartMutation,
  type TariffSchedulesQuery,
  type UpdatePlacingVariantsMutationVariables,
  useAddTariffScheduleMutation,
  useCancelPlacingVariantsEditMutation,
  useEditTariffScheduleCancelMutation,
  useEditTariffScheduleKeepAliveMutation,
  useEditTariffScheduleStartMutation,
  useEditTariffScheduleUpdateMutation,
  useGetPlacingVariantsQuery,
  useGetTariffScheduleQuery,
  useKeepAlivePlacingVariantsEditMutation,
  usePvmCancelEditMutation,
  usePvmCompleteMutation,
  usePvmGenerateMutation,
  usePvmKeepAliveMutation,
  usePvmStartMutation,
  useSendTariffScheduleInWorkMutation,
  useStartPlacingVariantsEditMutation,
  useTariffSchedulesQuery,
  useUpdatePlacingVariantsMutation,
} from '@/graphql/generated';
import { useAccommodationObjectsStore } from '@/parts/objects/store/accommodationObjects';

export type Tariffs = TariffSchedulesQuery['tariffSchedules2'];
export type RemoteTariffType = ArrayElementType<NonNullable<Tariffs>['remote']>;
export type TariffType = GetTariffScheduleQuery['tariffSchedule2'];
export type PlacingVariantsType = GetPlacingVariantsQuery['placingVariants']['placingVariants'];

const tariffs = ref<Tariffs>();
const activeTariff = ref<TariffType>();
const activeTariffPlacingVariants = ref<PlacingVariantsType>();

const PVMMaterData = ref<PvmStartMutation['placingVariantsMasterStart']>();

export const useTariffsStore = () => {
  const { activeAccount } = useAccommodationObjectsStore();

  const loadTariffs = async (contractNumber: number) => {
    const { request } = useTariffSchedulesQuery({
      contractNumber: contractNumber,
    });

    const response = await request();

    return response;
  };

  const getActiveTariffs = async () => {
    if (!activeAccount.value) {
      tariffs.value = undefined;

      return;
    }

    const { request, data, response } = useTariffSchedulesQuery({
      contractNumber: activeAccount.value.contractNumber,
    });

    await request();

    if (data.value) {
      tariffs.value = data.value.tariffSchedules2;
    }

    return response;
  };

  const setActiveTariff = (data: NonNullable<TariffType>) => {
    activeTariff.value = data;
  };

  const clearActiveTariff = () => {
    activeTariff.value = undefined;
  };

  const tariffsExtranet = toRef(() => tariffs.value?.extranet);

  const tariffsRemote = toRef(() => tariffs.value?.remote);

  const operations = toRef(() => tariffs.value?.operations);

  const createTariff = async (payload: Omit<AddTariffScheduleInput, 'contractNumber'>) => {
    if (!activeAccount.value) {
      return;
    }

    const { request } = useAddTariffScheduleMutation({
      addTariffScheduleInput: {
        contractNumber: activeAccount.value.contractNumber,
        ...payload,
      },
    });

    const response = await request();

    if (response?.data?.addTariffSchedule?.tariffSchedule) {
      setActiveTariff(response.data.addTariffSchedule.tariffSchedule);
    }

    return response;
  };

  const getActiveTariff = async (id: string) => {
    const { request, data, response } = useGetTariffScheduleQuery({
      tariffScheduleCid: id,
    });

    await request();

    if (data.value?.tariffSchedule2) {
      setActiveTariff(data.value.tariffSchedule2);
    }

    return response;
  };

  const getPlacingVariants = async () => {
    if (!activeTariff.value) {
      return;
    }

    const { request, data } = useGetPlacingVariantsQuery({
      hotelSetCid: activeTariff.value.hotelSetCid,
    });

    const response = await request();

    if (data.value?.placingVariants.placingVariants) {
      activeTariffPlacingVariants.value = data.value.placingVariants.placingVariants;
    }

    return response;
  };

  const PvmStartPVM = async (id: string) => {
    const { request } = usePvmStartMutation({
      tariffScheduleCid: id
    });

    const response = await request();

    if (response?.data?.placingVariantsMasterStart) {
      PVMMaterData.value = response.data.placingVariantsMasterStart;
    }

    return response;
  };

  const clearPVMMaterData = () => {
    PVMMaterData.value = undefined;
  };

  const PvmKeepAlive = async (id: string) => {
    const { request } = usePvmKeepAliveMutation({
      tariffScheduleCid: id
    });

    return request();
  };

  const PvmCancelEdit = async (id: string) => {
    const { request } = usePvmCancelEditMutation({
      tariffScheduleCid: id
    });

    return request();
  };

  const PvmGenerate = async (payload: PvmGenerateMutationVariables['input']) => {
    const { request, response } = usePvmGenerateMutation({
      input: payload,
    });

    await request();

    return response;
  };

  const PvmSubmit = async (payload: PvmCompleteMutationVariables['input']) => {
    const { request } = usePvmCompleteMutation({
      input: payload,
    });

    const response = await request();

    return response;
  };

  const {
    isResourceEdit: isPVMEdit,
    editResourceStart: startPVMEdit,
    isEditResourceStartLoading: isPVMStartLoading,
    editResourceCancel: cancelPVMEdit,
    editResourceUpdate: submitPVM,
  } = useLockResource('PlacingVariantMaster', PvmStartPVM, PvmKeepAlive, PvmCancelEdit, PvmSubmit);

  const startPlacingVariantsMaster = async () => {
    if (isPVMEdit.value) {
      return;
    }

    if (activeTariff.value) {
      return await startPVMEdit(String(activeTariff.value.tariffScheduleCid));
    }

    return;
  };

  const placingVariantsEditStart = async (id: string) => {
    const { request } = useStartPlacingVariantsEditMutation({
      tariffScheduleCid: id
    });

    const response = await request();

    if (response?.data?.placingVariantsEdit?.placingVariantsTarification) {
      activeTariffPlacingVariants.value = response.data.placingVariantsEdit.placingVariantsTarification;
    }

    return response;
  };

  const placingVariantsEditKeepAlive = async (id: string) => {
    const { request } = useKeepAlivePlacingVariantsEditMutation({
      tariffScheduleCid: id
    });

    return request();
  };

  const placingVariantsEditCancel = async (id: string) => {
    const { request } = useCancelPlacingVariantsEditMutation({
      tariffScheduleCid: id
    });

    return request();
  };

  const updateEditPlacingVariants = async (payload: UpdatePlacingVariantsMutationVariables) => {
    const { request } = useUpdatePlacingVariantsMutation(payload);

    const response = await request();

    if (response?.data?.placingVariantsUpdate?.placingVariantsTarification) {
      activeTariffPlacingVariants.value = response.data.placingVariantsUpdate.placingVariantsTarification;
    }

    return response;
  };

  const {
    editResourceStart: startEditPlacingVariants,
    isEditResourceStartLoading: isStartEditPlacingVariantsLoading,
    editResourceCancel: cancelEditPlacingVariants,
    editResourceUpdate: updatePlacingVariants,
  } = useLockResource(
    'editPlacingVariants',
    placingVariantsEditStart,
    placingVariantsEditKeepAlive,
    placingVariantsEditCancel,
    updateEditPlacingVariants,
  );

  const editStart = async (id: string) => {
    const { request } = useEditTariffScheduleStartMutation({
      input: { tariffScheduleCid: id },
    });

    const response = await request();

    return response;
  };

  const editKeepAlive = (id: string) => {
    const { request } = useEditTariffScheduleKeepAliveMutation({
      input: { tariffScheduleCid: id },
    });

    return request();
  };

  const editCancel = async (id: string) => {
    const { request } = useEditTariffScheduleCancelMutation({
      input: { tariffScheduleCid: id },
    });

    return request();
  };

  const editUpdate = async (payload: EditTariffScheduleUpdateMutationVariables['input']) => {
    const { request } = useEditTariffScheduleUpdateMutation({ input: payload });

    const response = await request();

    return response;
  };

  const {
    editResourceStart: editTariffScheduleStart,
    isEditResourceStartLoading: isEditTariffScheduleStartLoading,
    editResourceCancel: editTariffScheduleCancel,
    editResourceUpdate: editTariffScheduleUpdate,
    isEditResourceUpdateLoading: isEditTariffScheduleUpdateLoading,
  } = useLockResource('TariffSchedule', editStart, editKeepAlive, editCancel, editUpdate);

  const sendTariffToModeration = async (id: string) => {
    const { request } = useSendTariffScheduleInWorkMutation({
      tariffScheduleCid: id,
    });

    const response = await request();

    if (response?.data?.sendTariffScheduleInWork?.tariffSchedule2View) {
      setActiveTariff(response.data.sendTariffScheduleInWork.tariffSchedule2View);
    }

    return response;
  };

  return {
    tariffsExtranet,
    tariffsRemote,
    operations,
    activeTariff,
    activeTariffPlacingVariants,
    PVMMaterData,

    getActiveTariffs,
    loadTariffs,

    createTariff,
    getPlacingVariants,

    getActiveTariff,
    setActiveTariff,
    clearActiveTariff,

    startPlacingVariantsMaster,
    cancelPVMEdit,
    submitPVM,
    PvmGenerate,
    isPVMStartLoading,
    clearPVMMaterData,

    editTariffScheduleStart,
    isEditTariffScheduleStartLoading,
    isEditTariffScheduleUpdateLoading,
    editTariffScheduleCancel,
    editTariffScheduleUpdate,

    startEditPlacingVariants,
    isStartEditPlacingVariantsLoading,
    cancelEditPlacingVariants,
    updatePlacingVariants,

    sendTariffToModeration,
  };
};
